/**
 * 単体ページの表示の寿命(#306)。
 *
 * **いつ作り、いつ捨てるかだけを決める。** 作るもの(盤面のシェル / 共有フォルダのページ)は
 * 呼び出し側(`main.ts`)が渡す —— DOM に依存しないので `node` 環境で試せる(ADR-0019)。
 *
 * - **`pagehide` では必ず捨てる**(WebGL を解放する —— ブラウザごとに同時数の上限がある。
 *   [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md) の「`destroy` を必須にする」)。
 *   **bfcache に入るとき(`persisted: true`)も捨てる** —— bfcache の間に WebGL・動画のデコーダを持たない
 * - **`pageshow` は、捨てた後で bfcache から戻ったとき(`persisted: true`)だけ作り直す。**
 *   戻ると文書がそのまま復元され、入口のスクリプトは走り直さない —— 作り直さないと**空白のまま**になる
 * - **最初の表示の `pageshow`(`persisted: false`)では作らない**(入口が作っている。二重に作らない)
 *
 * **閲覧者が入れた素材は、戻ると外れる**(作り直すため)。bfcache が効かずに読み直したときと同じ
 * (同意書 Q1 —— bfcache の間も持ち続ける案と比べた記録がある)。
 */

/** 表示を捨てる。 */
export type Unmount = () => void;

/** 表示を作る。**捨てる関数を返す。** */
export type Mount = () => Unmount;

export interface PageLifecycle {
  /** `pagehide`。**表示していれば捨てる**(続けて呼んでも 1 度だけ)。 */
  hide(): void;
  /** `pageshow`。**捨てた後で、bfcache から戻ったとき(`persisted`)だけ作り直す。** */
  show(persisted: boolean): void;
}

/** **最初に 1 度作る。** 以後は `pagehide` を `hide`、`pageshow` を `show` へ渡す。 */
export function pageLifecycle(mount: Mount): PageLifecycle {
  let unmount: Unmount | null = mount();
  return {
    hide() {
      if (unmount === null) return;
      /*
       * **呼ぶ前に手放す。** 捨てる途中で投げても同じものを捨て直さず、
       * 戻ったときには作り直せる(残したままだと「表示している」と見なして作らない)。
       */
      const current = unmount;
      unmount = null;
      current();
    },
    show(persisted) {
      if (!persisted || unmount !== null) return;
      unmount = mount();
    },
  };
}
